import React, { useEffect, useState } from "react"
import Tab from "../Tab/Tab"
import { ESignIcon, UploadDocsIcon } from "../Icons/Icons"
import DocsTableList from "./DocsTabTable/DocsTabTable"
import { useDispatch, useSelector } from "react-redux"
import UploadNewDocumentModal from "../UploadNewDocument/UploadNewDocumentModal"
import { showSnackBar } from "../Snackbar/Snackbar"
import { deleteDocuments, getDocuments, getDocumentsTypes } from "../../features/documents/documents"
import DocsEsignTabTable from "./DocsTabTable/DocsEsignTabTable";
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider";
import { Skeleton } from "../Skeleton/Skeleton"
import { StorageGetItem, USER } from "../../lib/storage"
import { LuFileText, LuPenLine } from "react-icons/lu"
import { getContactData } from "../../features/customer/customerSlice"
import { getLeadDetails } from "../../features/contacts/contacts"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { DOCUMENTS_POA_ADDRESS_NOTICE, ESIGN_DOCS_SIGNER_EVIDENCE, PCA_FOR_AFFILIATES } from "../../config/featureFlagKeys"
import { DOCUMENTS_AUDIO_FLAG } from "../../constants/callRecordings"
import { REQUEST_ERRORS_FILES } from "../../constants/requestErrorFlags"
import PoaAddressNoticeButton from "./PoaAddressNotice/PoaAddressNoticeButton"
import SigningLinkPanel from "./SigningLinkPanel/SigningLinkPanel"

const EVIDENCE_VIEWER_ROLES = ['customer', 'user']
const POA_NOTICE_ROLES = ['customer', 'user']
const NO_DOCUMENT_ACCESS = { canUploadAudio: false, canViewDocs: false }

const DocsTab = ({ userId, contact, signingLinkOn = false }) => {
  const dispatch = useDispatch()
  const audioEnabled = useSelector(selectFeatureFlag(DOCUMENTS_AUDIO_FLAG))
  const filesStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_FILES))
  const [documentAccess, setDocumentAccess] = useState(NO_DOCUMENT_ACCESS)
  const [signingLinkOffered, setSigningLinkOffered] = useState(false)
  const savedActiveTab = localStorage.getItem('activeDocsTab');
  const [activeTab, setActiveTab] = useState(savedActiveTab || 'uploadedDocument')
  const [tableData, setTableData] = useState([])
  const [docsLoaded, setDocsLoaded] = useState(false)
  const [isUpdate, setIsUpdate] = useState(false)
  const [types, setTypes] = useState([])
  const [uploadedTypeId, setUploadedTypeId] = useState(null)
  const [existingSubfolders, setExistingSubfolders] = useState([])
  const { triggerLoading, triggerBar } = useMyContextLoading();
  const [userRole, setUserRole] = useState('')
  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])
  const evidenceFlagOn = useSelector(selectFeatureFlag(ESIGN_DOCS_SIGNER_EVIDENCE))
  const pcaForAffiliates = useSelector(selectFeatureFlag(PCA_FOR_AFFILIATES))
  const poaNoticeOn = useSelector(selectFeatureFlag(DOCUMENTS_POA_ADDRESS_NOTICE))
  const showEvidence = evidenceFlagOn && EVIDENCE_VIEWER_ROLES.includes(userRole)
  // WS #2228909: the PCA is for PACK's reps, but the flag reaches every affiliate of the brand. So an
  // affiliate gets the tab only for a client of an office offered the card split (the lead GET says so).
  // Unknown until read, and hidden if the read fails: as for every affiliate before the flag.
  const [cardSplitOffice, setCardSplitOffice] = useState(false)
  useEffect(() => {
    if (userRole !== 'affiliate' || !pcaForAffiliates || !userId) return
    Promise.resolve(dispatch(getLeadDetails(userId)))
      .then((result) => setCardSplitOffice(result?.payload?.data?.contactGroupConfig?.feeShape?.cardSplit === true))
      .catch(() => setCardSplitOffice(false))
  }, [userRole, pcaForAffiliates, userId, dispatch])
  const canSeeESign = !!userRole && (userRole !== 'affiliate' || (pcaForAffiliates && cardSplitOffice))
  // The last tab is remembered across clients, so E-Sign falls back to Uploaded Documents where its tab is hidden
  // (and until the role is read).
  const shownTab = activeTab === 'eSign' && !canSeeESign ? 'uploadedDocument' : activeTab

  useEffect(() => {
    if (!audioEnabled || !userId) return
    dispatch(getContactData(userId)).then((response) => {
      const contact = response?.payload?.data
      setDocumentAccess({
        canUploadAudio: contact?.canUploadAudio === true,
        canViewDocs: contact?.canViewDocs === true,
      })
    })
  }, [audioEnabled, userId, dispatch])

  useEffect(() => {
    localStorage.setItem('activeDocsTab', activeTab);
  }, [activeTab])

  const handleTabChange = (tab) => {
    setActiveTab(tab)
  }
  const getDocs = async () => {
    triggerBar(true)
    const response = await dispatch(getDocuments({ offset: 0, limit: 1000, userId: userId }))
    setTableData([])
    if (response?.payload?.data?.docs && response?.payload?.data?.docs?.length > 0) {
      const docs = response.payload.data.docs
      const subfolders = [...new Set(docs.map(d => d.subfolder).filter(Boolean))]
      setExistingSubfolders(subfolders)
      docs.forEach((item) => {
        const newDocs = {
          id: item?._id,
          dateUser: { date: item?.updatedAt, user: item?.user },
          ...item
        }
        setTableData((prevDocs) => [...prevDocs, newDocs])
      })
    } else {
      console.log('No payment data available.')
    }
    if (response.error) {
      showSnackBar("error", "Something went wrong!")
    } else if (response) {
    }
    setDocsLoaded(true)
    triggerBar(false)
  }

  const deleteDoc = async (key) => {
    triggerLoading(true)
    const response = await dispatch(deleteDocuments(key))
    if (response?.payload?.data?.key) showSnackBar("success", "Document Is Successfully Deleted")
    if ((audioEnabled || filesStrict) && response?.error) {
      showSnackBar("error", typeof response.payload === 'string' ? response.payload : 'Unable to delete the document')
    }
    setIsUpdate(!isUpdate)
    triggerLoading(false)
  }

  const getStatusesDocument = async () => {
    const response = await dispatch(getDocumentsTypes())
    const filteredData = response?.payload?.data?.filter(item => item.value !== "esignedDoc")
    setTypes(filteredData)
    setUploadedTypeId(filteredData?.find((item) => item.value === 'uploadedEsigneDoc')?._id ?? null)
  }
  useEffect(() => {
    const fetch = async () => {
      await getDocs()
      await getStatusesDocument()
    }
    fetch()
  }, [])

  useEffect(() => {
    const fetch = async () => {
      await getDocs()
    }
    fetch()
  }, [isUpdate])

  return (
    <div className="mb-6 flex w-full">
      <div className="w-full">
        {signingLinkOn && <SigningLinkPanel userId={userId} contact={contact} userRole={userRole} onOfferChange={setSigningLinkOffered} />}
        <div className="px-6 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 pb-6 w-full">
          <div className="flex flex-wrap gap-x-10 whitespace-nowrap">
            <Tab classes="text-md flex items-center"
              imgLink={<LuFileText size={18} className={shownTab === "uploadedDocument" ? 'text-text-primary' : 'text-text-secondary'} />}
              isActive={shownTab === "uploadedDocument"} onClick={() => handleTabChange('uploadedDocument')}>
              Uploaded Documents
            </Tab>
            {/* WS #2228909: affiliates too with PCA_FOR_AFFILIATES on, for the Payment Change Addendum (their only template), for PACK's clients. */}
            {canSeeESign && <Tab classes="text-md flex items-center"
              imgLink={<LuPenLine size={18} className={shownTab === "eSign" ? 'text-text-primary' : 'text-text-secondary'} />}
              isActive={shownTab === "eSign"} onClick={() => handleTabChange('eSign')}>
              E-Sign & Templates
            </Tab>}
          </div>
          {
            shownTab === "uploadedDocument" &&
            <div className="flex flex-wrap items-center gap-y-2 whitespace-nowrap">
              {poaNoticeOn && POA_NOTICE_ROLES.includes(userRole) && <PoaAddressNoticeButton userId={userId} refreshKey={isUpdate} onGenerated={() => setIsUpdate((value) => !value)} />}
              <UploadNewDocumentModal dropdownData={types} userId={userId} setIsUpdate={setIsUpdate} isUpdate={isUpdate} existingSubfolders={existingSubfolders}
                canUploadAudio={documentAccess.canUploadAudio} />
            </div>
          }
        </div>
        <div>
          {shownTab === 'uploadedDocument' && <div className="w-full">
            {!docsLoaded ? (
              <div className="px-6 pb-6 flex flex-col gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} height={48} rounded="rounded-xl" />
                ))}
              </div>
            ) : (
              <DocsTableList dropdownData={types} uploadedTypeId={uploadedTypeId} data={tableData} deleteDoc={deleteDoc} setIsUpdate={setIsUpdate}
                isUpdate={isUpdate} existingSubfolders={existingSubfolders} showEvidence={showEvidence} canViewDocs={documentAccess.canViewDocs} />
            )}
          </div>}
          {shownTab === 'eSign' &&
            <DocsEsignTabTable userId={userId} userRole={userRole} showEvidence={showEvidence} uploadedTypeId={uploadedTypeId}
              signingLinkOn={signingLinkOn} signingLinkOffered={signingLinkOffered} pcaForAffiliates={pcaForAffiliates} />}
        </div>
      </div>
    </div>
  )
}

export default DocsTab
